import React, { useState } from "react";
import { Clock, FileText, ExternalLink, Share2, Heart, Mail, Users, Play, Download, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const EXPOSED_OFFICIALS = [
  {
    name: "Tiffany Sizemore",
    title: "Allegheny County Judge",
    violation: "Initiated and extended dependency jurisdiction based on fabricated emergencies. Refused to return offspring despite cleared kinship and lack of harm.",
    quote: "I find clear and convincing evidence of abuse and or neglect.",
  },
  {
    name: "Wrenna L. Watson",
    title: "Allegheny County Judge",
    violation: "Signed a custody removal order on 12/20/2024 targeting babies in Jamaica — outside U.S. jurisdiction — with no exigency, no hearing, and no notice.",
    quote: "While mother is presently here in the US, she has left this child in Jamaica…",
  },
  {
    name: "J. Nicholas Ranjan",
    title: "U.S. District Court Judge, W.D PA",
    violation: "Acknowledged abuse in 2:24-cv-1733 but refused relief, invoking Younger abstention. Ignored child endangerment under federal jurisdiction.",
    quote: "Petitioner has not established grounds for prospective relief... abstention applies.",
  },
  {
    name: "Carla Hobson",
    title: "Hearing Officer, Allegheny County",
    violation: "Issued findings against reunification based on hearsay and sealed records with no live testimony.",
    quote: "Sufficient evidence was presented...",
  },
  {
    name: "Scott Sullivan",
    title: "OCYF Caseworker, Allegheny County",
    violation: "Allowed abusive placements. Blocked kinship with cleared cousin. Forced dietary religious violations.",
    quote: "I'M NOT WORRIED ABOUT YOUR LAW SUIT AT ALL",
  },
  {
    name: "Lawrence Walter",
    title: "OCYF Caseworker Supervisor",
    violation: "Refused kinship placement. Mocked religious concerns. Lied about checking background.",
    quote: "You can't be a sovereign and citizen... it's an oxymoron.",
  },
  {
    name: "Erin Dalton",
    title: "DHS Director",
    violation: "Ignored all complaints. No follow-up to repeated calls and letters since 11/8/2024.",
    quote: "",
  },
  {
    name: "Jamie Caterino",
    title: "Assistant Chief, Munhall (Badge #4742)",
    violation: "Oversaw 11/18/2024 home invasion. No warrant. Participated in child seizure without danger or due process.",
    quote: "We don't need a warrant.",
  },
];

const DOCUMENTS_VAULT = [
  {
    title: "Supreme Court Filing",
    description: "Full Supreme Court filing received 6.20.2025",
    url: "https://drive.google.com/file/d/1AJyF5um3pBXUmgYEaWxof7O6A129WpmN/view?usp=sharing",
  },
  {
    title: "Initial CYF Claims",
    description: "What CYF initially claimed to try and get my kids",
    url: "https://drive.google.com/file/d/1pE7LdVmiOutJDkR50gi5-qGEran4xMDL/view?usp=drive_link",
  },
  {
    title: "Fabricated Emergency",
    description: "They fabricated an 'emergency' 1 month after daughter's asthma attack to kidnap kids",
    url: "https://drive.google.com/file/d/1RyuUt2OmIrPwEYrHbBl96AlAJlSdltCm/view?usp=sharing",
  },
  {
    title: "Federal Court Acknowledgment",
    description: "Judge Ranjan's ruling acknowledging abuse but refusing relief",
    url: "https://drive.google.com/file/d/1WEadFSHF2eyBpy9zBTdrN77g8qtw-fTo/view?usp=drive_link",
  },
  {
    title: "Judge Watson's Order",
    description: "Custody order for babies in Jamaica - no hearing, no seal",
    url: "https://drive.google.com/file/d/1U9F7DZVdnxpucCiCtLtLFYewARYT_IeH/view?usp=drive_link",
  },
  {
    title: "Complaints Filed",
    description: "Partial picture of complaints filed with DOJ, Governor Shapiro, Congress",
    url: "https://drive.google.com/file/d/1YrtqcP3RIEhZ-Qfg-fou92CILmriSxS3/view?usp=sharing",
  },
];

const VIDEOS = [
  {
    title: "Abuse Confirmed on Recording - Foster Mom Berates Daughter",
    url: "https://www.youtube.com/watch?v=xeqjtqhYMMs",
    channel: "The Woman in Gold - The Qadash Sheep",
  },
  {
    title: "Redemption Skank (Let My People Go!)",
    url: "https://www.youtube.com/watch?v=A-Jw4hPSIcQ",
    channel: "Shaba Sharah",
  },
  {
    title: "No Shalum fi di Wicked (Enoch 96-98)",
    url: "https://www.youtube.com/watch?v=QphV8yCPQoE",
    channel: "Shaba Sharah",
  },
  {
    title: "Pharaoh Nuh Run Tings",
    url: "https://www.youtube.com/watch?v=CnlBK1nlw4Y",
    channel: "Shaba Sharah",
  },
];

export default function FightFiTash() {
  const [activeTab, setActiveTab] = useState("overview");

  return (
    <div className="min-h-screen bg-slate-950" data-testid="fightfitash-page">
      {/* Hero */}
      <section className="bg-scarlett text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <span className="text-kingdom-gold text-sm font-bold uppercase tracking-wider">
            #FightFiTash
          </span>
          <h1 className="heading-hero mt-2 mb-4 text-white">
            STATE-SANCTIONED CHILD ABUSE COVER-UP
          </h1>
          <p className="text-xl text-white/90 max-w-3xl">
            Federal judges know abuse occurred in State Custody. They still hide under abstention.
            This site exposes every judge, clerk, and oath-breaker involved.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="https://cash.app/$JonesaicaYahsharal" target="_blank" rel="noopener noreferrer">
              <Button className="bg-kingdom-gold hover:bg-kingdom-gold text-black font-bold">
                <Heart className="w-4 h-4 mr-2" />
                Donate to the Fight
              </Button>
            </a>
            <Button
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              onClick={() => navigator.clipboard.writeText(window.location.href)}
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share This Page
            </Button>
          </div>
        </div>
      </section>

      {/* Days Counter */}
      <section className="bg-black text-white py-8 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 text-kingdom-gold mb-2">
            <Clock className="w-5 h-5" />
            <span className="text-sm uppercase tracking-wider font-bold">Days Since Abduction</span>
          </div>
          <a
            href="http://tickcounter.com/countup/442684/days-since-tash-was-abducted"
            target="_blank"
            rel="noopener noreferrer"
            className="text-5xl font-bold text-white hover:text-kingdom-gold transition-colors"
          >
            220+ Days
          </a>
          <p className="text-slate-400 mt-2">Live Counter →</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-slate-900 border border-slate-800 mb-8 flex flex-wrap h-auto">
              <TabsTrigger value="overview" className="text-white data-[state=active]:bg-scarlett">
                Overview
              </TabsTrigger>
              <TabsTrigger value="exposed" className="text-white data-[state=active]:bg-scarlett">
                Exposed Officials
              </TabsTrigger>
              <TabsTrigger value="documents" className="text-white data-[state=active]:bg-scarlett">
                Document Vault
              </TabsTrigger>
              <TabsTrigger value="media" className="text-white data-[state=active]:bg-scarlett">
                Media
              </TabsTrigger>
              <TabsTrigger value="action" className="text-white data-[state=active]:bg-scarlett">
                Take Action
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="text-white">
              <div className="grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-slate-900 p-8 rounded-lg border border-slate-800">
                    <h2 className="font-heading text-2xl text-white mb-4">The Case</h2>
                    <div className="space-y-4 text-slate-300">
                      <p>
                        The government abducted my offspring. They were abused in state custody. 
                        I filed in court. Every judge ignored the evidence.
                      </p>
                      <p>
                        This is not a minor delay. It came after 4 Third Circuit appeals were sabotaged, 
                        filings withheld, and state actors trafficked biological offspring under unsealed, 
                        unsigned orders. SCOTUS was notified and is doing nothing.
                      </p>
                      <p>
                        <strong className="text-kingdom-gold">Tash now faces a 6th rejection and move as of June 5, 2025.</strong>
                      </p>
                    </div>
                  </div>

                  <div className="bg-slate-900 p-8 rounded-lg border border-slate-800">
                    <h2 className="font-heading text-2xl text-white mb-4">Key Facts</h2>
                    <ul className="space-y-3 text-slate-300">
                      <li className="flex gap-3">
                        <AlertTriangle className="w-5 h-5 text-kingdom-gold flex-shrink-0 mt-0.5" />
                        <span>Children abducted on 11/18/2024 without warrant or due process</span>
                      </li>
                      <li className="flex gap-3">
                        <AlertTriangle className="w-5 h-5 text-kingdom-gold flex-shrink-0 mt-0.5" />
                        <span>Judge Watson signed custody order for babies IN JAMAICA - outside US jurisdiction</span>
                      </li>
                      <li className="flex gap-3">
                        <AlertTriangle className="w-5 h-5 text-kingdom-gold flex-shrink-0 mt-0.5" />
                        <span>Federal court acknowledged abuse but refused relief citing "Younger abstention"</span>
                      </li>
                      <li className="flex gap-3">
                        <AlertTriangle className="w-5 h-5 text-kingdom-gold flex-shrink-0 mt-0.5" />
                        <span>SCOTUS clerks confirmed receipt of filing - no docketing, no review</span>
                      </li>
                      <li className="flex gap-3">
                        <AlertTriangle className="w-5 h-5 text-kingdom-gold flex-shrink-0 mt-0.5" />
                        <span>Religious dietary violations ignored by OCYF</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="space-y-6">
                  <Card className="bg-slate-900 border-slate-800 text-white">
                    <CardHeader>
                      <CardTitle className="text-kingdom-gold">SCOTUS COMPLICITY</CardTitle>
                    </CardHeader>
                    <CardContent className="text-slate-300">
                      <p className="text-sm">
                        Clerks Mary and Katie confirmed receipt. No docketing. No panel. 
                        No judicial review. This is judicial obstruction, not accident.
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-scarlett/20 border-scarlett text-white">
                    <CardHeader>
                      <CardTitle className="text-white">28 U.S.C. § 1691</CardTitle>
                    </CardHeader>
                    <CardContent className="text-slate-300">
                      <p className="text-sm">
                        "All writs and process issuing from a court of the United States 
                        shall be under the seal of the court and signed by the clerk thereof."
                      </p>
                      <a
                        href="https://www.law.cornell.edu/uscode/text/28/1691"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-kingdom-gold text-sm mt-2 inline-flex items-center gap-1"
                      >
                        View Law <ExternalLink className="w-3 h-3" />
                      </a>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* Exposed Officials Tab */}
            <TabsContent value="exposed" className="text-white">
              <h2 className="font-heading text-2xl mb-6">Exposed - Di Oath Breakers</h2>
              <p className="text-slate-400 mb-8">
                These are not anonymous systems. These are names, titles, and state-paid officials 
                who ignored the truth. They violated oaths. They trafficked our offspring. 
                And their silence is documented.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {EXPOSED_OFFICIALS.map((official) => (
                  <Card key={official.name} className="bg-slate-900 border-slate-800 text-white">
                    <CardHeader className="pb-2">
                      <div className="text-xs text-scarlett uppercase tracking-wider font-bold mb-1">
                        EXPOSED - DI OATH BREAKERS
                      </div>
                      <CardTitle className="text-xl text-white">{official.name}</CardTitle>
                      <p className="text-kingdom-gold text-sm">{official.title}</p>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-400 text-sm mb-3">
                        <strong className="text-scarlett">VIOLATION:</strong> {official.violation}
                      </p>
                      {official.quote && (
                        <p className="text-slate-500 text-sm italic border-l-2 border-scarlett pl-3">
                          "{official.quote}"
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Documents Vault Tab */}
            <TabsContent value="documents" className="text-white">
              <h2 className="font-heading text-2xl mb-6">The File Vault</h2>
              <p className="text-slate-400 mb-8">
                Wicked Courts tried fi bury di truth. But the record shows otherwise. 
                These files prove the abuse, obstruction, and oath-breaking.
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {DOCUMENTS_VAULT.map((doc) => (
                  <a
                    key={doc.title}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-slate-900 border border-slate-800 p-6 rounded-lg hover:border-kingdom-gold transition-colors block"
                  >
                    <FileText className="w-8 h-8 text-kingdom-gold mb-4" />
                    <h3 className="font-bold text-white mb-2">{doc.title}</h3>
                    <p className="text-slate-400 text-sm">{doc.description}</p>
                    <span className="text-kingdom-gold text-sm mt-4 inline-flex items-center gap-1">
                      View Document <ExternalLink className="w-3 h-3" />
                    </span>
                  </a>
                ))}
              </div>
            </TabsContent>

            {/* Media Tab */}
            <TabsContent value="media" className="text-white">
              <h2 className="font-heading text-2xl mb-6">Media & Music</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {VIDEOS.map((video) => (
                  <a
                    key={video.title}
                    href={video.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-slate-900 border border-slate-800 p-6 rounded-lg hover:border-kingdom-gold transition-colors block"
                  >
                    <Play className="w-8 h-8 text-kingdom-gold mb-4" />
                    <h3 className="font-bold text-white mb-2">{video.title}</h3>
                    <p className="text-slate-400 text-sm">{video.channel}</p>
                  </a>
                ))}
              </div>
            </TabsContent>

            {/* Take Action Tab */}
            <TabsContent value="action" className="text-white">
              <h2 className="font-heading text-2xl mb-6">Take Action - Get Involved</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card className="bg-slate-900 border-slate-800 text-white">
                  <CardHeader>
                    <Heart className="w-8 h-8 text-kingdom-gold mb-2" />
                    <CardTitle>Donate</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-400 text-sm mb-4">
                      This battle is being fought with legal filings, public exposure, and spiritual fire. 
                      Every donation helps cover filing fees, printing costs, and distribution.
                    </p>
                    <a href="https://cash.app/$JonesaicaYahsharal" target="_blank" rel="noopener noreferrer">
                      <Button className="w-full bg-kingdom-gold hover:bg-kingdom-gold text-black">
                        Donate Now - CashApp
                      </Button>
                    </a>
                  </CardContent>
                </Card>

                <Card className="bg-slate-900 border-slate-800 text-white">
                  <CardHeader>
                    <Users className="w-8 h-8 text-kingdom-gold mb-2" />
                    <CardTitle>Volunteer</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-400 text-sm mb-4">
                      Help deliver affidavits, organize teach-ins, distribute truth packs, 
                      or run street outreach. This is a movement—not a moment!
                    </p>
                    <a
                      href="https://docs.google.com/forms/d/e/1FAIpQLSe5V5QxF922a3XYIZCGveUf-THGQ83ZqislyeUQil2fKtwugA/viewform?usp=header"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button className="w-full btn-outline-royal border-white text-white">
                        Sign Up to Volunteer
                      </Button>
                    </a>
                  </CardContent>
                </Card>

                <Card className="bg-slate-900 border-slate-800 text-white">
                  <CardHeader>
                    <Mail className="w-8 h-8 text-kingdom-gold mb-2" />
                    <CardTitle>Write Letters</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-400 text-sm mb-4">
                      Flood Babylon – Send Truth to Power. Write public officials, courts, and newsrooms. 
                      We provide templates and addresses.
                    </p>
                    <a
                      href="https://docs.google.com/document/d/1WfOmVsZl5PkTxavc62pGqz4qUMsl506FT5FZhY75koM/edit?usp=sharing"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button className="w-full btn-outline-royal border-white text-white">
                        Access Letter Templates
                      </Button>
                    </a>
                  </CardContent>
                </Card>

                <Card className="bg-slate-900 border-slate-800 text-white">
                  <CardHeader>
                    <Share2 className="w-8 h-8 text-kingdom-gold mb-2" />
                    <CardTitle>Spread the Word</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-400 text-sm mb-4">
                      Your voice is the weapon. Share this page. Call out the oath-breakers. 
                      ASK QUESTIONS ABOUT TASH! Help the world see what they're hiding!
                    </p>
                    <Button
                      className="w-full btn-outline-royal border-white text-white"
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        alert("Link copied!");
                      }}
                    >
                      Copy & Share Link
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-scarlett py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white/90 text-lg italic">
            "Free by blood. Protected by YaHUaH. No contract, no consent, no court fi Babylon."
          </p>
        </div>
      </section>
    </div>
  );
}
